import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { GooglePlacesAutocomplete } from '@/components/ui/google-places-autocomplete';
import { Manager } from '@/types';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';

export interface Installer {
  id?: string;
  name: string; // Maps to company_name
  address: string; // Maps to street
  city: string;
  state: string;
  zip_code: string;
  county: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  user_id?: string | null;
  internal_pm_manager_id?: string | null;
  internal_pm_manager_name?: string | null;
  installer_pm_manager_id?: string | null;
  installer_pm_manager_name?: string | null;
  biz_dev_rep_id?: string | null;
  biz_dev_rep_name?: string | null;
  email: string | null;
  phone: string | null;
  cost_ppw: number | null;
}

interface Adder {
  id: number;
  name: string;
  display_order: number;
}

interface InstallerAdderCost {
  adder_id: number;
  adder_name: string;
  cost: number | null;
}

interface InstallerFormProps {
  initialData: Installer | null;
  onSubmit: (data: Installer | Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => Promise<string>;
  onCancel: () => void;
  isSubmitting: boolean;
  managers: Manager[];
  isEditing: boolean;
}

const residentialAdders: Adder[] = [
  { id: 1, name: 'EV Charger', display_order: 12 },
  { id: 2, name: 'MPU/ESC (Main Panel Upgrade)', display_order: 21 },
  { id: 3, name: 'ESC Relocation', display_order: 10 },
  { id: 4, name: 'ESC Service Upgrade', display_order: 11 },
  { id: 5, name: 'Dirt Trenching Level 1 (0 - 75ft)', display_order: 5 },
  { id: 6, name: 'Dirt Trenching Level 2 (76 - 100ft)', display_order: 6 },
  { id: 7, name: 'Dirt Trenching Level 3 (101 - 150ft)', display_order: 7 },
  { id: 8, name: 'Dirt Trenching Level 4 (150ft+)', display_order: 8 },
  { id: 9, name: 'Hardscape Trenching', display_order: 14 },
  { id: 10, name: 'Whole Home Any Battery', display_order: 32 },
  { id: 11, name: 'Mounting Adder (concrete tile, metal and flat roofs that are NON ballasted: S, W, Flat)', display_order: 18 },
  { id: 12, name: 'Flat Roof Adder (TPO/Foam)(ballasted)', display_order: 13 },
  { id: 13, name: 'Mounting Adder (ground mount) 7kW or Less', display_order: 20 },
  { id: 14, name: 'Mounting Adder (ground mount) 7.1kW or More', display_order: 19 },
  { id: 15, name: 'Span Panel', display_order: 26 },
  { id: 16, name: 'Pipe Vent Relocation', display_order: 23 },
  { id: 17, name: 'Sub Panel (new)', display_order: 29 },
  { id: 18, name: 'Sub Panel (replacement)', display_order: 30 },
  { id: 19, name: 'Stucco/Drywall Finish Work', display_order: 28 },
  { id: 20, name: 'Derate w/Breaker', display_order: 4 },
  { id: 21, name: 'Lineside/Loadside Tap (IPC/Hot Tap/HI Tie In)', display_order: 17 },
  { id: 22, name: 'Critter Guard', display_order: 3 },
  { id: 23, name: '240V Outlet', display_order: 1 },
  { id: 24, name: '3+ Floors (storys)', display_order: 2 },
  { id: 25, name: 'Roofing', display_order: 24 },
  { id: 26, name: 'Other', display_order: 22 },
  { id: 27, name: 'Engineering Report', display_order: 9 },
  { id: 28, name: 'Steep Roof', display_order: 27 },
  { id: 29, name: 'Sun Shield', display_order: 31 },
  { id: 30, name: 'Solar CPA', display_order: 25 },
  { id: 31, name: 'Home Owner Rebate', display_order: 15 },
  { id: 32, name: 'Illinois Site Survey', display_order: 16 },
  { id: 197, name: 'No Adders', display_order: 0 },
].sort((a, b) => a.display_order - b.display_order);

export function InstallerForm({ initialData, onSubmit, onCancel, isSubmitting, managers, isEditing }: InstallerFormProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<Installer | Omit<Installer, 'id' | 'created_at' | 'updated_at'>>(
    initialData || {
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      county: null,
      is_active: true,
      user_id: null,
      internal_pm_manager_id: null,
      internal_pm_manager_name: null,
      installer_pm_manager_id: null,
      installer_pm_manager_name: null,
      biz_dev_rep_id: null,
      biz_dev_rep_name: null,
      email: null,
      phone: null,
      cost_ppw: null,
    }
  );
  const [adderCosts, setAdderCosts] = useState<InstallerAdderCost[]>(
    residentialAdders.map((adder) => ({
      adder_id: adder.id,
      adder_name: adder.name,
      cost: null,
    }))
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingAdderCosts, setLoadingAdderCosts] = useState(false);

  // Load existing adder costs when editing
  React.useEffect(() => {
    const loadExistingAdderCosts = async () => {
      if (isEditing && initialData?.id) {
        setLoadingAdderCosts(true);
        try {
          // Get the tenant_id using the utility function
          const tenantId = await getCurrentTenantId();

          const { data, error } = await supabase
            .from('installer_adder_cost')
            .select('adder_id, adder_name, cost')
            .eq('installer_id', initialData.id)
            .eq('tenant_id', tenantId);

          if (error) {
            console.error('Error loading adder costs:', error);
          } else if (data) {
            // Update adder costs with existing values
            setAdderCosts(prev =>
              prev.map(adder => {
                const existing = data.find(d => d.adder_id === adder.adder_id);
                return existing ? { ...adder, cost: existing.cost } : adder;
              })
            );
          }
        } catch (error) {
          console.error('Error loading adder costs:', error);
        } finally {
          setLoadingAdderCosts(false);
        }
      }
    };

    loadExistingAdderCosts();
  }, [isEditing, initialData?.id]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Company name is required';
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (formData.cost_ppw !== null && (isNaN(formData.cost_ppw) || formData.cost_ppw < 0)) {
        newErrors.cost_ppw = 'Cost per watt must be a non-negative number';
      }
    } else if (step === 2) {
      adderCosts.forEach((adder, index) => {
        if (adder.cost !== null && (isNaN(adder.cost) || adder.cost < 0)) {
          newErrors[`adder_${adder.adder_id}`] = `Cost for ${adder.adder_name} must be a non-negative number`;
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent, skipAdderCosts: boolean = false) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (step === 1 && !skipAdderCosts) {
      setStep(2);
      return;
    }
    if (isEditing && !('id' in formData)) {
      toast({
        title: 'Error',
        description: 'Installer ID is required for updates.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const installerId = await onSubmit(formData);

      // Only save adder costs if we're on step 2
      if (step === 2) {
        await saveInstallerAdderCosts(installerId, adderCosts.filter((adder) => adder.cost !== null));
        toast({
          title: 'Success',
          description: 'Installer and adder costs saved successfully.',
        });
      } else if (skipAdderCosts) {
        // When explicitly skipping adder costs (Save Installer button clicked)
        toast({
          title: 'Success',
          description: 'Installer details saved successfully.',
        });
      }

      onCancel();
    } catch (error: any) {
      console.error('Error saving installer or adder costs:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save installer or adder costs.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveInstaller = async () => {
    if (!validateForm()) {
      return;
    }
    if (isEditing && !('id' in formData)) {
      toast({
        title: 'Error',
        description: 'Installer ID is required for updates.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const installerId = await onSubmit(formData);
      toast({
        title: 'Success',
        description: 'Installer details saved successfully.',
      });
      onCancel();
    } catch (error: any) {
      console.error('Error saving installer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save installer.',
        variant: 'destructive',
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' && (name === 'email' || name === 'phone' || name === 'cost_ppw') ? null : value,
      ...(name === 'cost_ppw' && value !== '' ? { cost_ppw: parseFloat(value) } : {}),
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAdderCostChange = (adderId: number, value: string) => {
    setAdderCosts((prev) =>
      prev.map((adder) =>
        adder.adder_id === adderId
          ? { ...adder, cost: value === '' ? null : parseFloat(value) }
          : adder
      )
    );
    if (errors[`adder_${adderId}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`adder_${adderId}`];
        return newErrors;
      });
    }
  };

  const handleAddressSelect = (address: any) => {
    const street = `${address.street_number || ''} ${address.route || ''}`.trim();
    const city = address.locality || '';
    const state = address.administrative_area_level_1 || '';
    const zip_code = address.postal_code || '';
    const county = address.administrative_area_level_2 || null;

    setFormData((prev) => ({
      ...prev,
      address: street,
      city,
      state,
      zip_code,
      county,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value === 'none' ? null : value,
      ...(name === 'internal_pm_manager_id' && {
        internal_pm_manager_name:
          value === 'none'
            ? null
            : managers.find((m) => m.id === value)?.first_name + ' ' + managers.find((m) => m.id === value)?.last_name,
      }),
      ...(name === 'installer_pm_manager_id' && {
        installer_pm_manager_name:
          value === 'none'
            ? null
            : managers.find((m) => m.id === value)?.first_name + ' ' + managers.find((m) => m.id === value)?.last_name,
      }),
      ...(name === 'biz_dev_rep_id' && {
        biz_dev_rep_name:
          value === 'none'
            ? null
            : managers.find((m) => m.id === value)?.first_name + ' ' + managers.find((m) => m.id === value)?.last_name,
      }),
    }));
  };

  const saveInstallerAdderCosts = async (installerId: string, adderCosts: InstallerAdderCost[]) => {
    try {
      // Get the tenant_id using the utility function
      const tenantId = await getCurrentTenantId();

      // If editing, delete existing adder costs first
      if (isEditing) {
        const { error: deleteError } = await supabase
          .from('installer_adder_cost')
          .delete()
          .eq('installer_id', installerId)
          .eq('tenant_id', tenantId);

        if (deleteError) {
          throw new Error(`Failed to delete existing adder costs: ${deleteError.message}`);
        }
      }

      // Insert new adder costs
      if (adderCosts.length > 0) {
        const { error } = await supabase.from('installer_adder_cost').insert(
          adderCosts.map((adder) => ({
            installer_id: installerId,
            adder_id: adder.adder_id,
            adder_name: adder.adder_name,
            cost: adder.cost,
            tenant_id: tenantId
          }))
        );
        if (error) {
          throw new Error(`Failed to save adder costs: ${error.message}`);
        }
      }
      // Successfully saved adder costs for installer
    } catch (error: any) {
      console.error('Error saving adder costs:', error);
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 py-4">
      {step === 1 && (
        <div className="grid gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="name" className="w-40 min-w-[160px] text-right">Company Name *</Label>
            <div className="flex-1">
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter company name"
                disabled={isSubmitting}
                className="w-full"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="address" className="w-40 min-w-[160px] text-right">Address</Label>
            <div className="flex-1">
              <GooglePlacesAutocomplete
                id="address"
                onAddressSelect={handleAddressSelect}
                placeholder="Enter address"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="email" className="w-40 min-w-[160px] text-right">Email</Label>
            <div className="flex-1">
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="Enter email"
                disabled={isSubmitting}
                className="w-full"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="phone" className="w-40 min-w-[160px] text-right">Phone</Label>
            <div className="flex-1">
              <Input
                id="phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                placeholder="Enter phone number"
                disabled={isSubmitting}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="cost_ppw" className="w-40 min-w-[160px] text-right">Cost per Watt</Label>
            <div className="flex-1">
              <Input
                id="cost_ppw"
                name="cost_ppw"
                type="number"
                step="0.01"
                value={formData.cost_ppw !== null ? formData.cost_ppw : ''}
                onChange={handleChange}
                placeholder="Enter cost per watt"
                disabled={isSubmitting}
                className="w-full"
              />
              {errors.cost_ppw && (
                <p className="text-sm text-destructive">{errors.cost_ppw}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="internal_pm_manager_id" className="w-40 min-w-[160px] text-right">Internal PM Manager</Label>
            <div className="flex-1">
              <SearchableSelect
                id="internal_pm_manager_id"
                value={formData.internal_pm_manager_id || 'none'}
                onValueChange={(value) => handleSelectChange('internal_pm_manager_id', value)}
                options={[
                  { label: 'None', value: 'none' },
                  ...managers.map((m) => ({
                    label: `${m.first_name} ${m.last_name}`,
                    value: m.id,
                  })),
                ]}
                placeholder="Select Internal PM Manager"
                disabled={isSubmitting}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="installer_pm_manager_id" className="w-40 min-w-[160px] text-right">Installer PM Manager</Label>
            <div className="flex-1">
              <SearchableSelect
                id="installer_pm_manager_id"
                value={formData.installer_pm_manager_id || 'none'}
                onValueChange={(value) => handleSelectChange('installer_pm_manager_id', value)}
                options={[
                  { label: 'None', value: 'none' },
                  ...managers.map((m) => ({
                    label: `${m.first_name} ${m.last_name}`,
                    value: m.id,
                  })),
                ]}
                placeholder="Select Installer PM Manager"
                disabled={isSubmitting}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="biz_dev_rep_id" className="w-40 min-w-[160px] text-right">Biz Dev Rep</Label>
            <div className="flex-1">
              <SearchableSelect
                id="biz_dev_rep_id"
                value={formData.biz_dev_rep_id || 'none'}
                onValueChange={(value) => handleSelectChange('biz_dev_rep_id', value)}
                options={[
                  { label: 'None', value: 'none' },
                  ...managers.map((m) => ({
                    label: `${m.first_name} ${m.last_name}`,
                    value: m.id,
                  })),
                ]}
                placeholder="Select Biz Dev Rep"
                disabled={isSubmitting}
                className="w-full"
                inDialog={false}
              />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-4 w-full">
          <h3 className="text-lg font-semibold">Set Adder Costs</h3>
          {loadingAdderCosts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto w-full">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[300px]">Adder Name</TableHead>
                    <TableHead className="min-w-[150px]">Cost ($)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adderCosts.map((adder) => (
                    <TableRow key={adder.adder_id}>
                      <TableCell className="whitespace-nowrap pr-4">{adder.adder_name}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={adder.cost !== null ? adder.cost : ''}
                          onChange={(e) => handleAdderCostChange(adder.adder_id, e.target.value)}
                          placeholder="Enter cost"
                          disabled={isSubmitting}
                          className="w-32"
                        />
                        {errors[`adder_${adder.adder_id}`] && (
                          <p className="text-sm text-destructive mt-1">{errors[`adder_${adder.adder_id}`]}</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        {step === 1 && (
          <>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            {isEditing && (
              <Button
                type="button"
                onClick={handleSaveInstaller}
                disabled={isSubmitting || !formData.name}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Installer'
                )}
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting || !formData.name}>
              {isEditing ? 'Update Adder Costs' : 'Next'}
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={isSubmitting}>
              Back
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Installer'
              )}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}